import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Share, PlusSquare, MoreVertical, Download, Apple, Play, Check } from "lucide-react";
import { SimplePage } from "@/components/SimplePage";
import { Nice } from "@/components/Nice";

const IPHONE = [
  [Smartphone, "Open NICE in Safari", "Other browsers on iPhone may not offer this option."],
  [Share, "Tap the Share button", "The square with an arrow pointing up, at the bottom (or top) of the screen."],
  [PlusSquare, "Choose “Add to Home Screen”", "Scroll down the menu if you don't see it right away."],
  [Check, "Tap “Add”", "The acorn icon named NICE appears on your Home Screen."],
];
const ANDROID = [
  [Smartphone, "Open NICE in Chrome", "Make sure you're signed in."],
  [MoreVertical, "Tap the ⋮ menu", "Top-right corner of the screen."],
  [Download, "Choose “Install app” or “Add to Home screen”", "Then tap Install or Add."],
  [Check, "Find the acorn icon", "NICE opens full-screen, just like an app."],
];

const Steps = ({ items, testId }) => (
  <ol className="pp-steps" data-testid={testId}>
    {items.map(([Icon, t, d], i) => (
      <li key={t}><span>{i + 1}</span><p><strong className="pp-step-title"><Icon className="inline h-4 w-4 mr-1" />{t}.</strong> {d}</p></li>
    ))}
  </ol>
);

const InstallButton = () => {
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const keep = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", keep);
    return () => window.removeEventListener("beforeinstallprompt", keep);
  }, []);
  if (!prompt) return null;
  return (
    <button className="pp-primary mt-4" onClick={() => { prompt.prompt(); setPrompt(null); }} data-testid="install-app-button">
      <Download className="h-5 w-5" /> Install NICE on this device
    </button>
  );
};

export default function GetTheApp() {
  const standalone = typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches;
  return (
    <SimplePage testId="get-app-page">
      <p className="pp-label">On your phone or tablet</p>
      <h1 className="pp-h1">Get the <Nice /> app</h1>
      <p className="pp-sub max-w-2xl">Use <Nice /> anywhere: same account, same children, same acorns and rewards on every device.</p>
      {standalone && <p className="pp-reward gold mt-4" data-testid="get-app-installed"><Check className="inline h-4 w-4" /> You're already using NICE from your Home Screen.</p>}
      <InstallButton />

      <section className="pp-card mt-8" data-testid="get-app-stores">
        <h2 className="pp-h2 flex items-center gap-2"><Apple className="h-5 w-5" /> <Play className="h-5 w-5" /> App Store & Google Play</h2>
        <p className="mt-2"><b>Coming soon.</b> When the <Nice /> app arrives in the stores, here's how it will work:</p>
        <ol className="pp-steps">
          <li><span>1</span><p><strong className="pp-step-title">Subscribe here on the website.</strong> Create your account and start your membership at this web address (or redeem a free-access code).</p></li>
          <li><span>2</span><p><strong className="pp-step-title">Download the app.</strong> Search for “NICE Colorful Acorn” in the App Store or Google Play.</p></li>
          <li><span>3</span><p><strong className="pp-step-title">Sign in with the same email and password.</strong> Your family, acorn jars, rewards and voices are all there.</p></li>
        </ol>
        <p className="text-sm opacity-75 mt-3">Until then, add <Nice /> to your Home Screen below. It works just like an app.</p>
      </section>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <section className="pp-card" data-testid="get-app-iphone">
          <h2 className="pp-h2">iPhone & iPad</h2>
          <Steps items={IPHONE} testId="iphone-steps" />
        </section>
        <section className="pp-card" data-testid="get-app-android">
          <h2 className="pp-h2">Android</h2>
          <Steps items={ANDROID} testId="android-steps" />
        </section>
      </div>

      <div className="pp-actions">
        <Link to="/signup" className="pp-primary" data-testid="get-app-signup-link">Create your account</Link>
        <Link to="/login" className="pp-ghost" data-testid="get-app-login-link">Sign in</Link>
      </div>
    </SimplePage>
  );
}
